import { api } from '../../../shared/lib/axios';

/**
 * API client do cadastro mestre de colaboradores (change
 * `pessoas-cadastro-mestre`).
 *
 * O interceptor HATEOAS do backend espalha objetos no envelope
 * (`{ version, _links, ...objeto }`) e põe arrays sob `data`. As
 * respostas `{ data: [...] }` chegam como `{ version, _links, data }`.
 */

export type EmploymentType = 'clt' | 'pj';
export type ContractKind = 'indeterminado' | 'determinado' | 'experiencia';
export type BondOrigin = 'manual' | 'candidato' | 'recontratacao' | 'migracao';
export type PersonStatus = 'pre_cadastrada' | 'em_validacao' | 'validada' | 'bloqueada' | 'inativa' | 'falecida';

export type BondStatus =
  | 'admissao_em_preparacao' | 'aguardando_documentos' | 'aguardando_exame_admissional'
  | 'aguardando_contabilidade' | 'aguardando_assinatura' | 'admissao_confirmada' | 'ativo'
  | 'em_experiencia' | 'afastado' | 'licenciado' | 'ferias' | 'aviso_previo'
  | 'desligamento_em_processamento' | 'desligado' | 'contratacao_cancelada' | 'transferido' | 'suspenso'
  | 'em_treinamento' | 'licenca_medica' | 'licenca_maternidade' | 'licenca_paternidade' | 'licenca_nao_remunerada'
  | 'aposentado' | 'falecido';

export interface PaginatedList<T> {
  data: T[];
  pagination: { limit: number; offset: number; count: number };
}

export interface Bond {
  id: string;
  userId: string;
  companyId: string;
  company: { id: string; tradeName: string; corporateName: string } | null;
  employmentType: EmploymentType;
  internalRegistration: string;
  payrollRegistration: string | null;
  admissionDate: string;
  contractKind: ContractKind;
  contractEndDate: string | null;
  probationEndDate: string | null;
  probationExtensionEndDate: string | null;
  endDate: string | null;
  status: BondStatus;
  statusLabel: string;
  origin: BondOrigin;
  applicationId: string | null;
  reconciliationPending: boolean;
  closed: boolean;
}

export interface CollaboratorListItem {
  id: string;
  name: string;
  socialName: string | null;
  cpf: string;
  email: string;
  personStatus: PersonStatus;
  currentBond: {
    id: string;
    status: BondStatus;
    internalRegistration: string;
    employmentType: EmploymentType;
    admissionDate: string;
    companyName: string | null;
  };
  orgUnitName: string | null;
  jobPositionName: string | null;
}

export interface MissingField {
  field: string;
  label: string;
}

export interface Collaborator {
  id: string;
  name: string;
  socialName: string | null;
  preferredName: string | null;
  cpf: string;
  cpfMasked: boolean;
  rg: string;
  email: string;
  phone: string;
  birthDate: string | null;
  birthCountry: string | null;
  nationality: string | null;
  birthCity: { id: number; name: string; uf: string } | null;
  sex: string | null;
  maritalStatus: string | null;
  motherName: string | null;
  fatherName: string | null;
  educationLevel: string | null;
  personStatus: PersonStatus;
  active: boolean;
  completeness: { percent: number; missing: MissingField[] };
  bonds: Bond[];
}

export interface CreatedCollaborator extends Collaborator {
  welcomeEmailSent?: boolean;
  passwordSetupLink?: { url: string; expiresInHours: number } | null;
  reusedPerson?: boolean;
}

export interface PersonalData {
  socialName?: string | null;
  preferredName?: string | null;
  birthDate?: string | null;
  birthCountry?: string | null;
  nationality?: string | null;
  birthCityId?: number | null;
  sex?: string | null;
  maritalStatus?: string | null;
  motherName?: string | null;
  fatherName?: string | null;
  educationLevel?: string | null;
}

export interface BondPayload {
  companyId: string;
  employmentType: EmploymentType;
  admissionDate: string;
  contractKind: ContractKind;
  contractEndDate?: string;
  probationEndDate?: string;
  probationExtensionEndDate?: string;
  internalRegistration?: string;
}

export interface CreateCollaboratorPayload extends PersonalData {
  name: string;
  cpf: string;
  rg: string;
  email: string;
  phone: string;
  bond: BondPayload;
}

export interface UpdateCollaboratorPayload extends PersonalData {
  name?: string;
  cpf?: string;
  rg?: string;
  email?: string;
  phone?: string;
  justification?: string;
}

export interface ConvertApplicationPayload extends PersonalData {
  rg?: string;
  bond: BondPayload;
}

export interface DuplicateMatch {
  id: string;
  name: string;
  cpf: string;
  email: string;
  reasons: Array<'cpf' | 'nome_nascimento' | 'email' | 'telefone'>;
}

export interface Pending {
  code: string;
  message: string;
  stage: 'cadastro' | 'ativacao';
}

export interface BondHistoryEntry {
  id: string;
  event: string;
  fromStatus: string | null;
  toStatus: string | null;
  payload: Record<string, unknown> | null;
  justification: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface ListCollaboratorsParams {
  search?: string;
  status?: BondStatus;
  orgUnitId?: string;
  jobPositionId?: string;
  limit?: number;
  offset?: number;
}

function paginated<T>(body: unknown): PaginatedList<T> {
  const b = (body ?? {}) as Partial<PaginatedList<T>>;
  return {
    data: Array.isArray(b.data) ? b.data : [],
    pagination: b.pagination ?? { limit: 0, offset: 0, count: 0 },
  };
}

function list<T>(body: unknown): T[] {
  if (Array.isArray(body)) return body as T[];
  const data = (body as { data?: unknown } | null)?.data;
  return Array.isArray(data) ? (data as T[]) : [];
}

function object<T>(body: unknown): T {
  const { version: _v, _links: _l, ...rest } = (body ?? {}) as Record<string, unknown>;
  return rest as T;
}

// --- Colaboradores -------------------------------------------------

export async function listarColaboradores(params: ListCollaboratorsParams): Promise<PaginatedList<CollaboratorListItem>> {
  const { data } = await api.get('/collaborators', { params });
  return paginated<CollaboratorListItem>(data);
}

export async function buscarColaborador(id: string): Promise<Collaborator> {
  const { data } = await api.get(`/collaborators/${id}`);
  return object<Collaborator>(data);
}

export async function buscarDuplicidades(params: {
  cpf?: string; name?: string; birthDate?: string; email?: string; phone?: string;
}): Promise<DuplicateMatch[]> {
  const { data } = await api.get('/collaborators/duplicates', { params });
  return list<DuplicateMatch>(data);
}

export async function criarColaborador(payload: CreateCollaboratorPayload): Promise<CreatedCollaborator> {
  const { data } = await api.post('/collaborators', payload);
  return object<CreatedCollaborator>(data);
}

export async function atualizarColaborador(id: string, payload: UpdateCollaboratorPayload): Promise<Collaborator> {
  const { data } = await api.patch(`/collaborators/${id}`, payload);
  return object<Collaborator>(data);
}

export async function validarColaborador(id: string): Promise<Collaborator> {
  const { data } = await api.post(`/collaborators/${id}/validate`);
  return object<Collaborator>(data);
}

// --- Vínculos ------------------------------------------------------

export async function criarVinculo(personId: string, payload: BondPayload): Promise<Bond> {
  const { data } = await api.post(`/collaborators/${personId}/bonds`, payload);
  return object<Bond>(data);
}

export async function atualizarVinculo(id: string, payload: Partial<BondPayload>): Promise<Bond> {
  const { data } = await api.patch(`/employment-bonds/${id}`, payload);
  return object<Bond>(data);
}

export async function pendenciasVinculo(id: string): Promise<Pending[]> {
  const { data } = await api.get(`/employment-bonds/${id}/pending`);
  return list<Pending>(data);
}

export async function historicoVinculo(id: string): Promise<BondHistoryEntry[]> {
  const { data } = await api.get(`/employment-bonds/${id}/history`);
  return list<BondHistoryEntry>(data);
}

/** Baixa o CSV de admissão e devolve o nome do arquivo. */
export async function exportarAdmissao(id: string): Promise<string> {
  const response = await api.get(`/employment-bonds/${id}/admission-export`, { responseType: 'blob' });
  const disposition = String(response.headers['content-disposition'] ?? '');
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'admissao.csv';
  const url = URL.createObjectURL(response.data as Blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
  return filename;
}

export async function registrarMatriculaFolha(id: string, payrollRegistration: string): Promise<Bond> {
  const { data } = await api.post(`/employment-bonds/${id}/payroll-registration`, { payrollRegistration });
  return object<Bond>(data);
}

export async function conciliarVinculo(id: string, payrollRegistration: string, justification: string): Promise<Bond> {
  const { data } = await api.post(`/employment-bonds/${id}/reconcile`, { payrollRegistration, justification });
  return object<Bond>(data);
}

export async function confirmarVinculo(id: string): Promise<Bond> {
  const { data } = await api.post(`/employment-bonds/${id}/confirm`);
  return object<Bond>(data);
}

export async function ativarVinculo(id: string): Promise<Bond> {
  const { data } = await api.post(`/employment-bonds/${id}/activate`);
  return object<Bond>(data);
}

export async function cancelarVinculo(id: string, justification: string): Promise<Bond> {
  const { data } = await api.post(`/employment-bonds/${id}/cancel`, { justification });
  return object<Bond>(data);
}

// --- Conversão de candidato ----------------------------------------

export async function vinculoDaCandidatura(applicationId: string): Promise<{ bondId: string; userId: string } | null> {
  const { data } = await api.get(`/employment-bonds/by-application/${applicationId}`);
  return (data as { bond?: { bondId: string; userId: string } | null }).bond ?? null;
}

export async function converterCandidatura(
  applicationId: string,
  payload: ConvertApplicationPayload,
): Promise<CreatedCollaborator> {
  const { data } = await api.post(`/employment-bonds/from-application/${applicationId}`, payload);
  return object<CreatedCollaborator>(data);
}

// --- Empresas (select do vínculo) ----------------------------------

export interface CompanyOption {
  id: string;
  tradeName: string;
  corporateName: string;
}

export async function listarEmpresasAtivas(): Promise<CompanyOption[]> {
  const { data } = await api.get('/companies', { params: { isActive: true, limit: 500 } });
  return list<CompanyOption>(data);
}

// --- Erros ---------------------------------------------------------

export interface ApiErrorBody {
  message?: string | string[];
  pendencias?: Array<{ code: string; message: string }>;
  personId?: string;
  bondId?: string;
}

export function corpoDoErro(err: unknown): ApiErrorBody {
  return ((err as { response?: { data?: ApiErrorBody } }).response?.data ?? {}) as ApiErrorBody;
}

export function statusDoErro(err: unknown): number | undefined {
  return (err as { response?: { status?: number } }).response?.status;
}

/** Mensagem de erro da API (string ou lista do ValidationPipe). */
export function mensagemDeErro(err: unknown, fallback: string): string {
  const msg = corpoDoErro(err).message;
  if (Array.isArray(msg)) return msg.join(' ');
  return typeof msg === 'string' && msg ? msg : fallback;
}
